import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';

import useApi, { UseInvalidateEndpoint } from 'common/useApi';
import AuthContext from 'common/auth';
import UiContext from 'common/UiContext/UiContext';

import Loading from 'components/Loading';

import './NotificationSettings.scss';

type PrefKey =
  | 'notify_worker_status'
  | 'notify_payout'
  | 'notify_reward'
  | 'notify_balance_threshold'
  | 'notify_email';

const CATEGORY_TOGGLES: { key: PrefKey; label: string; description: string }[] = [
  {
    key: 'notify_worker_status',
    label: 'Worker status',
    description: 'When a worker goes offline or comes back online.',
  },
  {
    key: 'notify_payout',
    label: 'Payouts',
    description: 'When a payout is sent to your address.',
  },
  {
    key: 'notify_reward',
    label: 'Block rewards',
    description: 'When the pool finds a block that credits your account.',
  },
  {
    key: 'notify_balance_threshold',
    label: 'Balance threshold',
    description: 'When your balance reaches the payout threshold.',
  },
];

const NotificationSettings = () => {
  const { user, refetch } = useContext(AuthContext)!;
  const { addToast } = useContext(UiContext);
  const invalidate = UseInvalidateEndpoint();
  const { isLoading, isSuccess, isError, mutate } = useApi(
    'post',
    '/api/me/update-notification-prefs',
  );

  // Local mirror so toggles feel instant; seeded from the account and kept in
  // sync if it changes elsewhere.
  const [prefs, setPrefs] = useState<Record<PrefKey, boolean>>({
    notify_worker_status: true,
    notify_payout: true,
    notify_reward: true,
    notify_balance_threshold: true,
    notify_email: true,
  });

  useEffect(() => {
    if (user) {
      setPrefs({
        notify_worker_status: user.notify_worker_status,
        notify_payout: user.notify_payout,
        notify_reward: user.notify_reward,
        notify_balance_threshold: user.notify_balance_threshold,
        notify_email: user.notify_email,
      });
    }
  }, [
    user?.notify_worker_status,
    user?.notify_payout,
    user?.notify_reward,
    user?.notify_balance_threshold,
    user?.notify_email,
  ]);

  useEffect(() => {
    if (isSuccess) {
      invalidate('/api/me');
      refetch();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) addToast!('Could not save notification settings');
  }, [isError]);

  const toggle = (key: PrefKey) => {
    const next = !prefs[key];

    setPrefs((p) => ({ ...p, [key]: next }));
    mutate({ [key]: next });
  };

  return (
    <>
      <h3>Notifications</h3>
      <div className={classNames({ box: true, isLoading, 'notification-settings': true })}>
        {isLoading && <Loading />}
        {CATEGORY_TOGGLES.map(({ key, label, description }) => (
          <label key={key} className='notification-toggle'>
            <div className='toggle-text'>
              <span className='toggle-label'>{label}</span>
              <span className='toggle-description'>{description}</span>
            </div>
            <input
              type='checkbox'
              checked={prefs[key]}
              onChange={() => toggle(key)}
            />
          </label>
        ))}
        <label className='notification-toggle email-toggle'>
          <div className='toggle-text'>
            <span className='toggle-label'>Email me</span>
            <span className='toggle-description'>
              Also send the notifications above to {user?.email} by email.
            </span>
          </div>
          <input
            type='checkbox'
            checked={prefs.notify_email}
            onChange={() => toggle('notify_email')}
          />
        </label>
      </div>
    </>
  );
};

export default NotificationSettings;
